import React, { useState, useCallback } from 'react'
import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react'
import { IconCategory } from '@deepguide-ai/types'
import { ChevronDown, Plus, MoreHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EmojiPicker } from '@/components/emoji-picker'
import { IconComponent } from '@/components/ui'
import { IconEditor } from '@/components/ui'
import { Separator } from '@/components/ui'
import { Switch } from '@/components/ui'
import { Button } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import { AccordionAttributes } from './accordion-extension'

interface AccordionNodeViewProps {
  node: ProseMirrorNode
  editor: Editor
  getPos: () => number | undefined
  updateAttributes: (attributes: Record<string, any>) => void
  deleteNode: () => void
  selected: boolean
}

export function AccordionNodeView({
  node,
  editor,
  getPos,
  updateAttributes,
  deleteNode,
  selected,
}: AccordionNodeViewProps) {
  const attrs = node.attrs as AccordionAttributes

  const title = attrs.title || 'Accordion'
  const description = attrs.description || ''
  const defaultOpen = attrs.defaultOpen || false
  const icon = attrs.icon || 'sparkles'
  const iconType = attrs.iconType || 'REGULAR'
  const iconCategory = attrs.iconCategory || 'LUCIDE'
  const iconColor = attrs.iconColor || '#3b82f6'
  const showIcon = attrs.showIcon || false

  const [isExpanded, setIsExpanded] = useState(true) // Start expanded for new accordions
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Temporary state for settings
  const [tempTitle, setTempTitle] = useState(title)
  const [tempDescription, setTempDescription] = useState(description)
  const [tempDefaultOpen, setTempDefaultOpen] = useState(defaultOpen)
  const [tempIcon, setTempIcon] = useState(icon)
  const [tempIconType, setTempIconType] = useState(iconType)
  const [tempIconColor, setTempIconColor] = useState(iconColor)
  const [tempShowIcon, setTempShowIcon] = useState(showIcon)
  const [tempIconCategory, setTempIconCategory] = useState(iconCategory)

  // Handle toggle accordion
  const toggleAccordion = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsExpanded(!isExpanded)
  }

  // Handle add new accordion
  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = getPos()
    if (pos === undefined) return

    // Insert new accordion after current one
    const newAccordionPos = pos + node.nodeSize

    editor.commands.insertContentAt(newAccordionPos, {
      type: 'Accordion',
      attrs: {
        title: 'Accordion Title',
        defaultOpen: true,
      },
      content: [
        {
          type: 'paragraph',
          content: [],
        },
      ],
    })
  }

  // Handle delete accordion
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    deleteNode()
  }

  // Handle save settings
  const saveSettings = useCallback(() => {
    const newAttributes: Partial<AccordionAttributes> = {}

    if (tempTitle.trim() !== title) {
      newAttributes.title = tempTitle.trim() || 'Accordion'
    }

    if (tempDescription !== description) {
      newAttributes.description = tempDescription
    }

    if (tempDefaultOpen !== defaultOpen) {
      newAttributes.defaultOpen = tempDefaultOpen
    }

    if (tempIcon !== icon) {
      newAttributes.icon = tempIcon
    }

    if (tempIconType !== iconType) {
      newAttributes.iconType = tempIconType
    }

    if (tempIconColor !== iconColor) {
      newAttributes.iconColor = tempIconColor
    }

    if (tempShowIcon !== showIcon) {
      newAttributes.showIcon = tempShowIcon
    }

    if (tempIconCategory !== iconCategory) {
      newAttributes.iconCategory = tempIconCategory
    }

    if (Object.keys(newAttributes).length > 0) {
      updateAttributes(newAttributes)
    }

    setIsPopoverOpen(false)
  }, [
    tempTitle,
    title,
    tempDescription,
    description,
    tempDefaultOpen,
    defaultOpen,
    tempIcon,
    iconCategory,
    tempIconCategory,
    icon,
    tempIconType,
    iconType,
    tempIconColor,
    iconColor,
    tempShowIcon,
    showIcon,
    updateAttributes,
  ])

  // Handle cancel settings
  const cancelSettings = useCallback(() => {
    setTempTitle(title)
    setTempDescription(description)
    setTempDefaultOpen(defaultOpen)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setTempIconCategory(iconCategory)
    setIsPopoverOpen(false)
  }, [
    title,
    description,
    defaultOpen,
    icon,
    iconType,
    iconColor,
    showIcon,
    iconCategory,
  ])

  // Handle open settings
  const openSettings = useCallback(() => {
    setTempTitle(title)
    setTempDescription(description)
    setTempDefaultOpen(defaultOpen)
    setTempIconCategory(iconCategory)
    setTempIcon(icon)
    setTempIconType(iconType)
    setTempIconColor(iconColor)
    setTempShowIcon(showIcon)
    setIsPopoverOpen(true)
  }, [
    title,
    description,
    defaultOpen,
    icon,
    iconType,
    iconColor,
    showIcon,
    iconCategory,
  ])

  return (
    <NodeViewWrapper
      className={cn(
        'accordion-item group/item border-border relative my-5 overflow-hidden rounded-xl border border-solid p-0',
        selected && 'ProseMirror-selectednode'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className='accordion-header bg-accent flex cursor-pointer items-center justify-between gap-2 px-5 py-4'
        onClick={toggleAccordion}
        contentEditable={false}
      >
        <div className='flex flex-1 items-start gap-1.5 leading-[28px] font-semibold'>
          <div className='flex-shrink-0 pt-1.5'>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isExpanded ? 'rotate-180 transform' : ''
              )}
            />
          </div>

          <div
            className={`flex-shrink-0 ${iconCategory === 'EMOJI' ? 'h-[22px] pt-[1.5px]' : 'pt-1'}`}
          >
            {showIcon && (
              <IconComponent
                iconName={icon}
                iconCategory={iconCategory}
                iconType={iconType}
                iconColor={iconColor}
                size={20}
                className={`transition-colors ${
                  iconCategory === 'EMOJI'
                    ? 'h-[20px] w-[20px] !text-[20px]'
                    : ''
                }`}
              />
            )}
          </div>

          <div className='flex-1'>
            {title}
            {description && (
              <div className='text-muted-foreground mt-1 text-sm font-normal'>
                {description}
              </div>
            )}
          </div>
        </div>

        {editor.isEditable && (
          <div
            className={`flex items-center gap-0 opacity-0 group-hover/item:opacity-100`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant='ghost' size='menuAction' onClick={handleAdd}>
              <Plus className='text-foreground h-4 w-4' />
            </Button>

            {(isHovered || isPopoverOpen) && (
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant='ghost'
                    size='menuAction'
                    onClick={(e) => {
                      e.stopPropagation()
                      openSettings()
                    }}
                  >
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </PopoverTrigger>

                <PopoverContent
                  className='w-[340px] rounded-2xl border border-sidebar-border bg-white p-0 shadow-[0_8px_40px_0_rgba(0,0,0,0.08)]'
                  align='end'
                  side='bottom'
                  sideOffset={8}
                >
                  <div className='flex flex-col'>
                    <div className='flex items-center justify-between border-b border-sidebar-border pl-6 pr-4 py-5'>
                      <h3 className='font-[Fraunces] text-[20px] leading-[1.2] font-medium text-sidebar-foreground'>
                        Accordion Settings
                      </h3>
                      <Button
                        variant='ghost'
                        size='iconSm'
                        onClick={cancelSettings}
                        className='h-8 w-8 text-[#86888F] hover:bg-muted hover:text-[#43464D]'
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>

                    <div className='space-y-4 px-6 py-6'>
                      <div className='flex flex-col gap-2'>
                        <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                          Title
                        </label>
                        <div className='flex h-[42px] items-center gap-2'>
                          <div className='flex h-[42px] w-11 shrink-0 items-center justify-center  bg-white'>
                            <EmojiPicker
                              selectedIcon={tempShowIcon ? tempIcon : undefined}
                              iconColor={tempIconColor}
                              iconCategory={tempIconCategory}
                              onEmojiSelect={(emoji) => {
                                setTempShowIcon(true)
                                setTempIcon(emoji)
                                setTempIconCategory('EMOJI')
                              }}
                              onIconChange={(
                                iconName: string,
                                iconCategory: IconCategory
                              ) => {
                                if (iconName === '') {
                                  setTempIconCategory('LUCIDE')
                                  setTempShowIcon(false)
                                  return
                                }
                                setTempShowIcon(true)
                                setTempIcon(iconName)
                                setTempIconCategory(iconCategory)
                              }}
                              onColorChange={setTempIconColor}
                              isPopoverMode={true}
                            />
                          </div>
                          <input
                            type='text'
                            value={tempTitle}
                            onChange={(e) => setTempTitle(e.target.value)}
                            placeholder='Enter title...'
                            className='placeholder:text-[#85878D] focus-visible:ring-ring h-[42px] w-full flex-1 rounded-md border border-[#E3E1DD] bg-transparent px-3 text-[13px] leading-[1.2] font-normal text-[#43464D] transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                          />
                        </div>
                      </div>

                      <div className='flex flex-col gap-2'>
                        <label className='text-[13px] leading-[1.2] font-normal text-[#1F2024]'>
                          Description
                        </label>
                        <textarea
                          value={tempDescription}
                          onChange={(e) => setTempDescription(e.target.value)}
                          placeholder='Enter description...'
                          rows={3}
                          className='placeholder:text-[#85878D] focus-visible:ring-ring w-full resize-none rounded-md border border-[#E3E1DD] bg-transparent px-3 py-2 text-[13px] leading-[1.3] font-normal text-[#43464D] focus:border-[#E3E1DD] focus:outline-none focus-visible:ring-1'
                        />
                      </div>


                    {/* <div>
                    <div className='flex justify-between items-center mb-4 w-full'>
                      <label className='text-xs leading-4 text-muted-foreground'>
                        Show Icon
                      </label>
                      <Switch
                        checked={tempShowIcon}
                        onCheckedChange={() => setTempShowIcon(!tempShowIcon)}
                      />
                    </div>

                    {tempShowIcon && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <IconEditor
                          selectedIcon={tempIcon}
                          iconType={tempIconType}
                          iconColor={tempIconColor}
                          onIconChange={setTempIcon}
                          onTypeChange={setTempIconType}
                          onColorChange={setTempIconColor}
                        />
                      </div>
                    )}
                  </div> */}
                    {/* <div className='flex flex-col gap-2'>
                      <label className='text-muted-foreground text-xs leading-4'>
                        Icon
                      </label>
                      <EmojiPicker
                        selectedIcon={tempShowIcon ? tempIcon : undefined}
                        iconColor={tempIconColor}
                        iconCategory={tempIconCategory}
                        onEmojiSelect={(emoji) => {
                          setTempShowIcon(true)
                          setTempIcon(emoji)
                          setTempIconCategory('EMOJI')
                        }}
                        onIconChange={(
                          iconName: string,
                          iconCategory: IconCategory
                        ) => {
                          if (iconName === '') {
                            setTempIconCategory('LUCIDE')
                            setTempShowIcon(false)
                            return
                          }
                          setTempShowIcon(true)
                          setTempIcon(iconName)
                          setTempIconCategory(iconCategory)
                        }}
                        onColorChange={setTempIconColor}
                        isPopoverMode={true}
                      />
                    </div> */}

                      <div className='flex items-center justify-between'>
                        <label className='text-[13px] leading-[1.2] font-normal text-[#86888F]'>
                        Open by Default
                        </label>
                        <Switch
                          checked={tempDefaultOpen}
                          onCheckedChange={() =>
                            setTempDefaultOpen(!tempDefaultOpen)
                          }
                        />
                      </div>
                    </div>


                    <div className='flex items-center justify-between border-t border-sidebar-border px-6 pt-[14px] pb-4'>
                      <Button
                        variant='destructive'
                        className='h-9 bg-transparent px-0 text-[13px] leading-[1.1] font-normal text-[#C24646] shadow-none hover:bg-transparent hover:text-[#C24646]'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(e)
                        }}
                      >
                        Delete
                      </Button>
                      <div className='flex items-center space-x-2'>
                        <Button
                          variant='outline'
                          className='h-9 w-24 rounded-lg border-[#E3E1DD] px-0 text-[13px] text-[#43464D] hover:bg-muted'
                          onClick={cancelSettings}
                        >
                          Cancel
                        </Button>
                        <Button
                          className='h-9 w-[120px] rounded-lg bg-sidebar-primary px-0 text-[13px] text-white hover:bg-[#6f8264]'
                          onClick={saveSettings}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          'accordion-content overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out [&_.slate-p]:!my-0',
          isExpanded
            ? 'max-h-[500px] opacity-100'
            : 'max-h-0 border-t-0 opacity-0'
        )}
      >
        <div className='px-5 py-5'>
          <NodeViewContent />
        </div>
      </div>
    </NodeViewWrapper>
  )
}
